import type { Organization, OrganizationDetail } from '@repo/sdk/gen'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle
} from '@repo/ui/card'
import { DetailField } from '@repo/ui/components/detail-field'
import { DetailFields } from '@repo/ui/components/detail-fields'
import {
	StorageBar,
	StorageSwatch,
	usageLevel
} from '@repo/ui/components/usage-meter'
import { useLocaleFormat } from '@repo/ui/hooks/use-locale-format'
import { ScrollArea } from '@repo/ui/scroll-area'
import { Separator } from '@repo/ui/separator'
import { cn, formatBytes } from '@repo/ui/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CircleAlert, TriangleAlert } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { OrganizationUsageCell } from '@/routes/(private)/organizations/-components/organization-metric-cells'
import { OrganizationDestruction } from '@/routes/(private)/organizations/$organizationId/-components/organization-destruction'
import { ProvisioningFailure } from '@/routes/(private)/organizations/$organizationId/-components/provisioning-failure'
import { ProvisioningLadder } from '@/routes/(private)/organizations/$organizationId/-components/provisioning-ladder'
import { SetOrganizationQuotaAction } from '@/routes/(private)/organizations/$organizationId/-components/set-organization-quota-action'
import { organizationDetailQueryOptions } from '@/routes/(private)/organizations/$organizationId/-query/query-options'

export const Route = createFileRoute(
	'/(private)/organizations/$organizationId/'
)({
	component: OrganizationOverview
})

/**
 * The machinery behind one customer, in the order the questions are asked:
 * **how far did the creation get**, **why did it stop**, **what does it hold**
 * and **is there anything to press**. Without this an organization that
 * converges converges in silence, and one that is stuck stays stuck until the
 * reseller's customer telephones.
 *
 * **Who is inside is the other tab**, and used to be a card at the bottom of
 * this one. A fourth question the four above cannot reach — *did anybody ever
 * get in* — is answered there, on the row of the person still waiting: a
 * `ready` organization whose first invitation went into a spam folder is green
 * on every reading on this page while containing nobody at all.
 *
 * The read itself, the name, the status and the lifecycle commands belong to
 * `route.tsx`, which draws them above both tabs.
 */
function OrganizationOverview() {
	const params = Route.useParams()
	const organization = useQuery(
		organizationDetailQueryOptions(params.organizationId)
	)

	// The parent route has already resolved this query and rendered its three
	// failure branches; reaching here means the record is in the cache. The
	// guard is for the frame between a navigation and that cache read, not a
	// second opinion about whether the customer exists.
	if (!organization.data) {
		return null
	}

	return <OrganizationSummary organization={organization.data} />
}

function OrganizationSummary({
	organization
}: {
	organization: OrganizationDetail
}) {
	return (
		<ScrollArea className='min-h-0 flex-1'>
			{/*
			 * One column, three cards in the order the questions are asked.
			 * It was a two-column grid with the cards on the right, which
			 * made sense while there were four of them; once the people and
			 * the first administrator moved to their own tab, Quota was left
			 * alone in a column of its own, a small box of two words beside
			 * a tall one. A stack reads better, and it takes the full width:
			 * a reading capped halfway across leaves a black half nobody can
			 * explain.
			 */}
			<div className='flex flex-col gap-6 px-6 py-6'>
				<ProvisioningCard organization={organization} />
				<IdentityCard organization={organization} />
				<QuotaCard organization={organization} />

				{/* Renders nothing until the organization has been
				    deactivated: the gate lives in the card, in
				    `destructionStage`, so it is one statement rather
				    than two that can disagree. */}
				<OrganizationDestruction organization={organization} />
			</div>
		</ScrollArea>
	)
}

function ProvisioningCard({ organization }: { organization: Organization }) {
	const { t } = useTranslation()
	const { formatDate } = useLocaleFormat()

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{t('operator-organizations:detail.provisioning')}
				</CardTitle>
			</CardHeader>
			<CardContent className='flex flex-col gap-6'>
				<ProvisioningLadder organization={organization} />

				<Separator />

				<DetailFields columns={3}>
					<DetailField
						label={t('operator-organizations:columns.created_at')}
						value={formatDate(organization.createdAt, 'P HH:mm')}
					/>
					<DetailField
						label={t(
							'operator-organizations:detail.attempts_label'
						)}
						value={t('operator-organizations:detail.attempts', {
							count: organization.provisioningAttempts
						})}
					/>
					<DetailField
						label={t(
							'operator-organizations:detail.last_attempt_label'
						)}
						value={
							// Null before the first attempt ever runs, and
							// `formatLocaleDate` would turn that into
							// "Invalid Date" rather than refusing.
							organization.provisioningLastAttemptAt === null
								? t(
										'operator-organizations:detail.last_attempt_never'
									)
								: formatDate(
										organization.provisioningLastAttemptAt,
										'P HH:mm'
									)
						}
					/>
				</DetailFields>

				{organization.status === 'failed' && (
					<ProvisioningFailure organization={organization} />
				)}
			</CardContent>
		</Card>
	)
}

/**
 * The two names an organization answers to. There is no legal name on the
 * contract; the second identifier is the slug, which is permanent and is what
 * people type to reach their sign-in page.
 */
function IdentityCard({ organization }: { organization: Organization }) {
	const { t } = useTranslation()

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{t('operator-organizations:detail.identity.title')}
				</CardTitle>
				<CardDescription>
					{t('operator-organizations:detail.identity.description')}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<DetailFields columns={1}>
					<DetailField
						label={t('operator-organizations:detail.identity.name')}
						value={organization.name}
					/>
					<DetailField
						label={t('operator-organizations:detail.identity.slug')}
						value={
							<span className='font-mono'>
								{organization.slug}
							</span>
						}
					/>
				</DetailFields>
			</CardContent>
		</Card>
	)
}

/**
 * How much the organization may store, as one bar in three parts — what its
 * files take now, what its administrators have assigned to people and is not
 * yet stored, what is left — with a figure per part and the quota beside them.
 * Then the two figures that are different numbers on purpose (`backoffice/frontend.md`, "The storage quota, and the
 * figure it is enforced against"). What its files take right now is live and
 * is what the quota is enforced against, so it is the one the bar is drawn
 * with. The daily measurement of the store stays beside it under its own
 * label and with its date: an old number on its own reads as a current one.
 *
 * Both states the operator can create by lowering the quota are said in
 * words: an organization at its quota refuses every upload, and one whose
 * assignments add up to more than it lets its administrators only lower them.
 *
 * Nothing to change once its data is being destroyed.
 */
function QuotaCard({ organization }: { organization: OrganizationDetail }) {
	const { t } = useTranslation()
	const { quotaBytes, quotaUsedBytes, quotaAssignedBytes } = organization
	// Assigned to people and not yet stored, and what is left to assign: the
	// two parts of the bar beside what is stored. Floored at zero, since the
	// operator may have set the quota below either.
	const reserved = Math.max(0, quotaAssignedBytes - quotaUsedBytes)
	const free =
		quotaBytes === null
			? null
			: Math.max(
					0,
					quotaBytes - Math.max(quotaUsedBytes, quotaAssignedBytes)
				)

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					{t('operator-organizations:detail.quota.title')}
				</CardTitle>
				{organization.status !== 'purging' &&
					organization.status !== 'purged' && (
						<CardAction>
							<SetOrganizationQuotaAction
								organization={organization}
							/>
						</CardAction>
					)}
			</CardHeader>
			<CardContent className='flex flex-col gap-4'>
				<div className='grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'>
					<div className='flex flex-col justify-center gap-2.5 rounded-lg border bg-muted/30 p-5'>
						<div className='text-muted-foreground text-xs'>
							{t('operator-organizations:detail.quota.limit')}
						</div>
						<div className='font-semibold text-2xl tabular-nums leading-tight'>
							{quotaBytes === null
								? t(
										'operator-organizations:detail.quota.no_limit'
									)
								: formatBytes(quotaBytes)}
						</div>
						{quotaBytes !== null && (
							<StorageBar
								assigned={quotaAssignedBytes}
								label={t(
									'operator-organizations:detail.quota.bar',
									{
										used: formatBytes(quotaUsedBytes),
										reserved: formatBytes(reserved),
										free: formatBytes(free ?? 0),
										quota: formatBytes(quotaBytes)
									}
								)}
								limit={quotaBytes}
								used={quotaUsedBytes}
							/>
						)}
						<p className='text-muted-foreground text-sm'>
							{t(
								quotaBytes === null
									? 'operator-organizations:detail.quota.description_unlimited'
									: 'operator-organizations:detail.quota.description'
							)}
						</p>
						{quotaBytes !== null && (
							<QuotaNotice
								assignedBytes={quotaAssignedBytes}
								quotaBytes={quotaBytes}
								usedBytes={quotaUsedBytes}
							/>
						)}
					</div>

					<dl className='flex flex-col justify-center gap-4'>
						<QuotaFigure
							label={t(
								'operator-organizations:detail.quota.in_use'
							)}
							swatch={quotaBytes === null ? undefined : 'used'}
							value={formatBytes(quotaUsedBytes)}
						/>
						{quotaBytes === null ? (
							<QuotaFigure
								label={t(
									'operator-organizations:detail.quota.assigned'
								)}
								value={formatBytes(quotaAssignedBytes)}
							/>
						) : (
							<>
								<QuotaFigure
									label={t(
										'operator-organizations:detail.quota.reserved'
									)}
									swatch='reserved'
									value={formatBytes(reserved)}
								/>
								<QuotaFigure
									label={t(
										'operator-organizations:detail.quota.free'
									)}
									swatch='free'
									value={formatBytes(free ?? 0)}
								/>
							</>
						)}
					</dl>
				</div>

				<Separator />

				<DetailFields columns={1}>
					<DetailField
						label={t(
							'operator-organizations:detail.quota.measured'
						)}
						value={
							<OrganizationUsageCell
								organization={organization}
							/>
						}
					/>
				</DetailFields>
			</CardContent>
		</Card>
	)
}

/**
 * The one sentence the meters cannot say: what follows from where they are.
 * Full outranks over-assigned, which outranks near — the first is the one that
 * stops every upload now.
 */
function QuotaFigure({
	className,
	label,
	value,
	swatch
}: {
	className?: string
	label: string
	value: string
	swatch?: 'used' | 'reserved' | 'free'
}) {
	return (
		<div className={cn('flex min-w-0 flex-col gap-0.5', className)}>
			<dt className='flex items-center gap-1.5 text-muted-foreground text-xs'>
				{swatch && <StorageSwatch part={swatch} />}
				{label}
			</dt>
			<dd className='font-semibold text-lg tabular-nums'>{value}</dd>
		</div>
	)
}

function QuotaNotice({
	quotaBytes,
	usedBytes,
	assignedBytes
}: {
	quotaBytes: number
	usedBytes: number
	assignedBytes: number
}) {
	const { t } = useTranslation()
	const level = usageLevel(usedBytes, quotaBytes)

	if (level === 'full') {
		return (
			<p className='flex items-start gap-1.5 text-destructive text-sm'>
				<CircleAlert className='mt-0.5 size-4 shrink-0' />
				{t('operator-organizations:detail.quota.full')}
			</p>
		)
	}

	if (assignedBytes > quotaBytes) {
		return (
			<p className='flex items-start gap-1.5 text-destructive text-sm'>
				<TriangleAlert className='mt-0.5 size-4 shrink-0' />
				{t('operator-organizations:detail.quota.over_assigned')}
			</p>
		)
	}

	if (level === 'near') {
		return (
			<p className='flex items-start gap-1.5 text-sm text-warning'>
				<TriangleAlert className='mt-0.5 size-4 shrink-0' />
				{t('operator-organizations:detail.quota.near', {
					free: formatBytes(quotaBytes - usedBytes)
				})}
			</p>
		)
	}

	return null
}
